/* eslint-disable @typescript-eslint/no-explicit-any */
import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { cubicOut } from 'svelte/easing'
import type { TransitionConfig } from 'svelte/transition'
import type { z } from 'zod'
import { t } from './translations'
import { Es, Gb, De, Fr, It } from 'svelte-flag-icons'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

type FlyAndScaleParams = {
  y?: number
  x?: number
  start?: number
  duration?: number
}

export const flyAndScale = (node: Element, params: FlyAndScaleParams = { y: -8, x: 0, start: 0.95, duration: 150 }): TransitionConfig => {
  const style = getComputedStyle(node)
  const transform = style.transform === 'none' ? '' : style.transform

  const scaleConversion = (valueA: number, scaleA: [number, number], scaleB: [number, number]) => {
    const [minA, maxA] = scaleA
    const [minB, maxB] = scaleB

    const percentage = (valueA - minA) / (maxA - minA)
    const valueB = percentage * (maxB - minB) + minB

    return valueB
  }

  const styleToString = (style: Record<string, number | string | undefined>): string => {
    return Object.keys(style).reduce((str, key) => {
      if (style[key] === undefined) return str
      return str + `${key}:${style[key]};`
    }, '')
  }

  return {
    duration: params.duration ?? 200,
    delay: 0,
    css: (t) => {
      const y = scaleConversion(t, [0, 1], [params.y ?? 5, 0])
      const x = scaleConversion(t, [0, 1], [params.x ?? 0, 0])
      const scale = scaleConversion(t, [0, 1], [params.start ?? 0.95, 1])

      return styleToString({
        transform: `${transform} translate3d(${x}px, ${y}px, 0) scale(${scale})`,
        opacity: t
      })
    },
    easing: cubicOut
  }
}

export const customErrorMap: z.ZodErrorMap = (issue, ctx) => {
  switch (issue.code) {
    case 'unrecognized_keys':
    case 'invalid_union':
    case 'invalid_arguments':
    case 'invalid_return_type':
    case 'invalid_date':
      return { message: t.get(`errors.${issue.code}`) }
    case 'invalid_string':
      if (issue.validation === 'email') {
        return { message: t.get('errors.email') }
      }

      return { message: t.get(`errors.${issue.code}`) }
    case 'invalid_type':
      if (issue.received === 'undefined') {
        return { message: t.get('errors.field_required') }
      }

      return { message: t.get('errors.invalid_type', { expected: issue.expected, received: issue.received } as any) }
    case 'invalid_enum_value':
      return { message: t.get('errors.invalid_enum_value') + ` ${issue.options.join(', ')}` }
    case 'too_small':
      return {
        message: t.get('errors.too_small', { minimum: issue.minimum.toString() } as any)
      }
    case 'too_big':
      return { message: t.get('errors.too_big', { maximum: issue.maximum.toString() } as any) }
    case 'not_multiple_of':
      return { message: t.get('errors.not_multiple_of', { multiple: issue.multipleOf.toString() } as any) }
    case 'custom':
      return { message: `Error. ${JSON.stringify(issue.params)}.` }
    default:
      return { message: ctx.defaultError }
  }
}

export const langValueToIcon = {
  'Español': Es,
  'English': Gb,
  'Deutsch': De,
  'Français': Fr,
  'Italiano': It,
}
type LangMap = {
  [key: string]: string;
}

export const langLabelToValue: LangMap = {
  'Español': 'es',
  'English': 'en',
  'Deutsch': 'de',
  'Français': 'fr',
  'Italiano': 'it'
}